import React, { useEffect, useState } from "react";
import {
    Send,
    Plus,
    Pencil,
    Trash2,
    Eye,
    Loader2,
    Mail,
    X,
    Check,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import RichTextEditor from "../../components/admin/RichTextEditor";

const STATUS_LABELS = {
    draft: { label: "Taslak", color: "bg-slate-100 text-slate-600" },
    sending: { label: "Gönderiliyor", color: "bg-amber-100 text-amber-700" },
    sent: { label: "Gönderildi", color: "bg-data-green/10 text-data-green" },
};

export default function NewsletterCampaigns() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [previewing, setPreviewing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/newsletter/campaigns");
            setItems(data.items || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const create = () => {
        setEditing({
            subject: "",
            preheader: "",
            html_content: "<p>Yeni bülten içeriği...</p>",
            sender_name: "Otel Pro",
            audience: "all",
            isNew: true,
        });
    };

    const editItem = async (id) => {
        try {
            const { data } = await api.get(`/admin/newsletter/campaigns/${id}`);
            setEditing({ ...data, isNew: false });
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const save = async () => {
        if (!editing.subject || editing.subject.length < 2) {
            toast.error("Konu boş olamaz.");
            return;
        }
        if (!editing.html_content || editing.html_content.length < 10) {
            toast.error("İçerik çok kısa.");
            return;
        }
        try {
            const payload = {
                subject: editing.subject,
                preheader: editing.preheader,
                html_content: editing.html_content,
                sender_name: editing.sender_name,
                audience: editing.audience,
            };
            if (editing.isNew) {
                await api.post("/admin/newsletter/campaigns", payload);
            } else {
                await api.put(`/admin/newsletter/campaigns/${editing.id}`, payload);
            }
            toast.success("Kaydedildi.");
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Kampanya silinecek. Onaylıyor musunuz?")) return;
        try {
            await api.delete(`/admin/newsletter/campaigns/${id}`);
            toast.success("Silindi.");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const sendNow = async (id) => {
        if (!window.confirm("Tüm aboneye e-posta gönderilecek. Onaylıyor musunuz?")) return;
        try {
            const { data } = await api.post(`/admin/newsletter/campaigns/${id}/send`);
            toast.success(`Gönderildi: ${data.sent}/${data.recipient_count} (Hata: ${data.failed})`);
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const testSend = async (id) => {
        const to = window.prompt("Test gönderimi için e-posta adresi:");
        if (!to) return;
        try {
            const { data } = await api.post(`/admin/newsletter/campaigns/${id}/test`, { to });
            if (data.sent) {
                toast.success(`Test gönderildi: ${to}`);
            } else {
                toast.warning(`Gönderilemedi (${data.skipped_reason || data.error})`);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const preview = async (id) => {
        try {
            const { data } = await api.get(`/admin/newsletter/campaigns/${id}/preview`);
            setPreviewing(data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div data-testid="admin-newsletters-page">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-5">
                <div>
                    <h1 className="text-2xl font-bold text-dark-navy inline-flex items-center gap-2">
                        <Send className="w-6 h-6 text-teal" /> Bülten Kampanyaları
                    </h1>
                    <p className="text-sm text-deep-blue/70 mt-1">
                        TipTap zengin metin editörü ile kampanya hazırlayın, test gönderin, tüm abonelere yayınlayın (Resend üzerinden).
                    </p>
                </div>
                <button
                    onClick={create}
                    className="bg-gold hover:bg-gold-700 text-dark-navy text-sm font-bold px-4 py-2 rounded-md inline-flex items-center gap-2"
                    data-testid="newsletter-new-btn"
                >
                    <Plus className="w-4 h-4" /> Yeni Kampanya
                </button>
            </div>

            <div className="bg-white rounded-lg shadow-md border border-slate-100 overflow-hidden">
                {loading ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12 inline-flex items-center justify-center gap-2 w-full">
                        <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center text-deep-blue/60 text-sm py-12">
                        <Mail className="w-10 h-10 mx-auto mb-3 text-teal/40" />
                        Henüz kampanya yok. <strong>Yeni Kampanya</strong> butonuna tıklayın.
                    </div>
                ) : (
                    <table className="w-full text-sm" data-testid="newsletter-campaigns-table">
                        <thead>
                            <tr className="bg-light-bg text-left text-xs uppercase tracking-wider text-deep-blue/70">
                                <th className="px-4 py-3">Konu</th>
                                <th className="px-4 py-3">Hedef</th>
                                <th className="px-4 py-3">Durum</th>
                                <th className="px-4 py-3">Gönderim</th>
                                <th className="px-4 py-3 w-56">İşlem</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((c) => {
                                const status = STATUS_LABELS[c.status] || { label: c.status, color: "bg-slate-100 text-slate-600" };
                                return (
                                    <tr key={c.id} className="border-t border-slate-100">
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-dark-navy text-sm">{c.subject}</div>
                                            <div className="text-xs text-deep-blue/60 mt-0.5">{c.preheader}</div>
                                        </td>
                                        <td className="px-4 py-3 text-xs uppercase font-semibold text-deep-blue/80">{c.audience}</td>
                                        <td className="px-4 py-3">
                                            <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold ${status.color}`}>{status.label}</span>
                                        </td>
                                        <td className="px-4 py-3 text-xs text-deep-blue/70">
                                            {c.sent_at ? new Date(c.sent_at).toLocaleDateString("tr-TR") : "—"}
                                            {c.recipient_count > 0 && (
                                                <div className="font-data text-[11px]">{c.sent_count || 0} / {c.recipient_count}</div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-1">
                                                <button onClick={() => preview(c.id)} title="Önizle" className="p-1.5 rounded-md hover:bg-light-bg">
                                                    <Eye className="w-4 h-4 text-deep-blue" />
                                                </button>
                                                {c.status !== "sent" && (
                                                    <>
                                                        <button onClick={() => editItem(c.id)} title="Düzenle" className="p-1.5 rounded-md hover:bg-light-bg">
                                                            <Pencil className="w-4 h-4 text-teal-700" />
                                                        </button>
                                                        <button onClick={() => testSend(c.id)} title="Test Gönder" className="p-1.5 rounded-md hover:bg-light-bg text-xs font-semibold text-deep-blue">
                                                            TEST
                                                        </button>
                                                        <button onClick={() => sendNow(c.id)} title="Tümüne Gönder" className="p-1.5 rounded-md hover:bg-light-bg" data-testid={`newsletter-send-${c.id}`}>
                                                            <Send className="w-4 h-4 text-data-green" />
                                                        </button>
                                                    </>
                                                )}
                                                <button onClick={() => remove(c.id)} title="Sil" className="p-1.5 rounded-md hover:bg-light-bg">
                                                    <Trash2 className="w-4 h-4 text-data-red" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {editing && (
                <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="newsletter-editor">
                    <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-3xl w-full my-8">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-dark-navy">{editing.isNew ? "Yeni Kampanya" : "Kampanya Düzenle"}</h2>
                            <button onClick={() => setEditing(null)} className="p-1.5 rounded-md hover:bg-light-bg">
                                <X className="w-4 h-4 text-deep-blue" />
                            </button>
                        </div>
                        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                            <div className="grid sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Konu</label>
                                    <input
                                        type="text"
                                        value={editing.subject || ""}
                                        onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                        data-testid="newsletter-subject"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Hedef Kitle</label>
                                    <select
                                        value={editing.audience || "all"}
                                        onChange={(e) => setEditing({ ...editing, audience: e.target.value })}
                                        className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm bg-white"
                                        data-testid="newsletter-audience"
                                    >
                                        <option value="all">Tüm aboneler</option>
                                        <option value="confirmed">Onaylanmış aboneler</option>
                                        <option value="unconfirmed">Onaylanmamışlar</option>
                                    </select>
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Önyazı (Preheader)</label>
                                <input
                                    type="text"
                                    value={editing.preheader || ""}
                                    onChange={(e) => setEditing({ ...editing, preheader: e.target.value })}
                                    className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">İçerik (TipTap)</label>
                                <div className="mt-1">
                                    <RichTextEditor
                                        value={editing.html_content || ""}
                                        onChange={(v) => setEditing({ ...editing, html_content: v })}
                                        placeholder="Bülten içeriği..."
                                        testId="newsletter-content-editor"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-deep-blue/80">Gönderici Adı</label>
                                <input
                                    type="text"
                                    value={editing.sender_name || "Otel Pro"}
                                    onChange={(e) => setEditing({ ...editing, sender_name: e.target.value })}
                                    className="mt-1 w-full px-3 py-2 rounded-md border border-slate-300 text-sm"
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-end gap-2 p-5 border-t border-slate-200">
                            <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-md bg-light-bg text-deep-blue text-sm font-semibold">
                                İptal
                            </button>
                            <button
                                onClick={save}
                                className="px-4 py-2 rounded-md bg-teal hover:bg-teal-700 text-dark-navy text-sm font-bold inline-flex items-center gap-1"
                                data-testid="newsletter-save"
                            >
                                <Check className="w-4 h-4" /> Kaydet
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {previewing && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="newsletter-preview">
                    <div className="bg-white rounded-lg shadow-2xl border border-slate-200 max-w-3xl w-full my-8">
                        <div className="flex items-center justify-between p-5 border-b border-slate-200">
                            <h2 className="text-base font-semibold text-dark-navy">Önizleme: {previewing.subject}</h2>
                            <button onClick={() => setPreviewing(null)} className="p-1.5 rounded-md hover:bg-light-bg">
                                <X className="w-4 h-4 text-deep-blue" />
                            </button>
                        </div>
                        <div className="p-3 max-h-[80vh] overflow-y-auto bg-light-bg">
                            <iframe title="preview" srcDoc={previewing.html} className="w-full bg-white" style={{ minHeight: "70vh", border: "1px solid #e2e8f0", borderRadius: "8px" }} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
